/**
 * Plan category bucket expansion for lumpsum plan lists.
 * Public API: usePlanCategoryExpansionState, usePlanCategoryList, usePlanCategoryBucket.
 * All expand/collapse policy and scroll behaviour lives here — lists and accordions only call these hooks.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PlanCategoryBucketKey, PlansActionFilter, PlansPlanMode } from '../api/types'
import {
  buildPlanCategoryBucketCompositeKey,
  getVisiblePlanCategoryBucketKeys,
  resolvePlanCategoryKey,
  type CategoryWithPlanBuckets
} from '../../utils/lumpsum/planListUtils'
import { useLumpsumPlansContext } from './lumpsumPlansContext'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PlanCategoryEntry<T extends CategoryWithPlanBuckets> = {
  category: T
  categoryIndex: number
}

type ExpansionScopeState = Record<string, Record<string, boolean>>

type ExpansionSyncTargets = {
  defaultExpandedKeys: Record<string, boolean>
  orderedCompositeKeys: string[]
}

type ExpansionScopeUpdate = {
  nextExpandedKeys: Record<string, boolean>
  scrollTarget: string | null
  shouldUpdateKeys: boolean
}

export type PlanCategoryExpansionState = {
  expandedBucketKeysByScope: ExpansionScopeState
  applyExpansionSync: (
    planMode: PlansPlanMode,
    actionFilter: PlansActionFilter,
    syncTargets: ExpansionSyncTargets
  ) => void
  toggleBucketExpanded: (
    planMode: PlansPlanMode,
    actionFilter: PlansActionFilter,
    bucketCompositeKey: string
  ) => void
  pendingBucketScrollKey: string | null
  clearPendingBucketScroll: () => void
}

// ---------------------------------------------------------------------------
// Expansion state (merged into LumpsumPlansProvider)
// ---------------------------------------------------------------------------

/** Holds expansion keyed by `${planMode}:${actionFilter}` so mounted hidden panels keep their own state. */
export function usePlanCategoryExpansionState(): PlanCategoryExpansionState {
  const [expandedBucketKeysByScope, setExpandedBucketKeysByScope] = useState<ExpansionScopeState>({})
  const [pendingBucketScrollKey, setPendingBucketScrollKey] = useState<string | null>(null)

  const expandedBucketKeysByScopeRef = useRef(expandedBucketKeysByScope)
  expandedBucketKeysByScopeRef.current = expandedBucketKeysByScope

  const clearPendingBucketScroll = useCallback(() => {
    setPendingBucketScrollKey(null)
  }, [])

  const applyExpansionSync = useCallback(
    (scopePlanMode: PlansPlanMode, scopeActionFilter: PlansActionFilter, syncTargets: ExpansionSyncTargets) => {
      const expandedScopeKey = buildExpansionScopeKey(scopePlanMode, scopeActionFilter)
      const previousForScope = expandedBucketKeysByScopeRef.current[expandedScopeKey] ?? {}
      const { nextExpandedKeys, scrollTarget, shouldUpdateKeys } = resolveScopeExpansionUpdate(
        previousForScope,
        syncTargets
      )

      setPendingBucketScrollKey(scrollTarget)

      if (!shouldUpdateKeys) {
        return
      }

      setExpandedBucketKeysByScope((previousByScope) => ({
        ...previousByScope,
        [expandedScopeKey]: nextExpandedKeys
      }))
    },
    []
  )

  const toggleBucketExpanded = useCallback(
    (scopePlanMode: PlansPlanMode, scopeActionFilter: PlansActionFilter, bucketCompositeKey: string) => {
      const expandedScopeKey = buildExpansionScopeKey(scopePlanMode, scopeActionFilter)

      setExpandedBucketKeysByScope((previousByScope) => {
        const previousForScope = previousByScope[expandedScopeKey] ?? {}

        return {
          ...previousByScope,
          [expandedScopeKey]: {
            ...previousForScope,
            [bucketCompositeKey]: !previousForScope[bucketCompositeKey]
          }
        }
      })
    },
    []
  )

  return useMemo(
    (): PlanCategoryExpansionState => ({
      expandedBucketKeysByScope,
      applyExpansionSync,
      toggleBucketExpanded,
      pendingBucketScrollKey,
      clearPendingBucketScroll
    }),
    [
      expandedBucketKeysByScope,
      applyExpansionSync,
      toggleBucketExpanded,
      pendingBucketScrollKey,
      clearPendingBucketScroll
    ]
  )
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

/** Categories visible for the current filter, plus expansion sync when data or filter changes. */
export function usePlanCategoryList<T extends CategoryWithPlanBuckets>(
  categories: T[] | null | undefined,
  actionFilter: PlansActionFilter,
  planMode: PlansPlanMode
): PlanCategoryEntry<T>[] {
  const {
    expandedBucketKeysByScope,
    applyExpansionSync,
    pendingBucketScrollKey,
    clearPendingBucketScroll
  } = useLumpsumPlansContext()

  const expandedScopeKey = buildExpansionScopeKey(planMode, actionFilter)
  const expandedBucketKeys = expandedBucketKeysByScope[expandedScopeKey] ?? {}

  const categoryEntries = useMemo(
    () => buildVisibleCategoryEntries(categories, actionFilter),
    [categories, actionFilter]
  )

  useEffect(() => {
    applyExpansionSync(planMode, actionFilter, buildExpansionSyncTargets(categoryEntries, actionFilter))
  }, [categoryEntries, actionFilter, planMode, applyExpansionSync])

  usePendingBucketScroll(pendingBucketScrollKey, expandedBucketKeys, clearPendingBucketScroll)

  return categoryEntries
}

/** Expansion state for a single bucket accordion. Builds the composite key internally. */
export type PlanCategoryBucketParams = {
  planMode: PlansPlanMode
  actionFilter: PlansActionFilter
  categoryId: string
  bucketKey: PlanCategoryBucketKey
}

export function usePlanCategoryBucket({
  planMode,
  actionFilter,
  categoryId,
  bucketKey
}: PlanCategoryBucketParams): {
  bucketCompositeKey: string
  isExpanded: boolean
  toggle: () => void
} {
  const { expandedBucketKeysByScope, toggleBucketExpanded } = useLumpsumPlansContext()
  const bucketCompositeKey = buildPlanCategoryBucketCompositeKey({ categoryKey: categoryId, bucketKey })
  const expandedScopeKey = buildExpansionScopeKey(planMode, actionFilter)
  const expandedBucketKeys = expandedBucketKeysByScope[expandedScopeKey] ?? {}

  return {
    bucketCompositeKey,
    isExpanded: expandedBucketKeys[bucketCompositeKey] ?? false,
    toggle: () => toggleBucketExpanded(planMode, actionFilter, bucketCompositeKey)
  }
}

// ---------------------------------------------------------------------------
// Internal — scroll
// ---------------------------------------------------------------------------

/** Toggle bucket scroll-into-view on filter/tab sync. */
export const BUCKET_SCROLL_INTO_VIEW_ENABLED = false

const BUCKET_SCROLL_MIN_VISIBLE_RATIO = 0.1
const BUCKET_SCROLL_TARGET_VIEWPORT_RATIO = 0.4

/**
 * Scroll runs after expansion renders in the DOM. Double rAF waits for the accordion
 * layout pass before querying the scroll target.
 */
function usePendingBucketScroll(
  pendingBucketScrollKey: string | null,
  expandedBucketKeys: Record<string, boolean>,
  clearPendingBucketScroll: () => void
): void {
  useEffect(() => {
    if (!BUCKET_SCROLL_INTO_VIEW_ENABLED) {
      if (pendingBucketScrollKey) {
        clearPendingBucketScroll()
      }
      return
    }

    if (!pendingBucketScrollKey || !expandedBucketKeys[pendingBucketScrollKey]) {
      return
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToExpandedBucket(pendingBucketScrollKey)
        clearPendingBucketScroll()
      })
    })
  }, [pendingBucketScrollKey, expandedBucketKeys, clearPendingBucketScroll])
}

// ---------------------------------------------------------------------------
// Expansion policy (pure)
// ---------------------------------------------------------------------------

/*
 * On filter/tab change (per scope):
 *   1. If any bucket is already expanded in this scope → keep that map as-is.
 *   2. Otherwise → defaults:
 *      - ALL: expand every sell bucket across categories (buy / under review / etc. stay collapsed;
 *        if there are no sells, nothing opens).
 *      - Specific filter: expand every visible bucket of that filter across categories.
 *   3. When BUCKET_SCROLL_INTO_VIEW_ENABLED: scroll to the first expanded bucket in display
 *      order (skip if ≥10% visible; align bucket top to 40% of the scroll viewport).
 */

function buildExpansionScopeKey(planMode: PlansPlanMode, actionFilter: PlansActionFilter): string {
  return `${planMode}:${actionFilter}`
}

export { buildExpansionScopeKey }

function buildVisibleCategoryEntries<T extends CategoryWithPlanBuckets>(
  categories: T[] | null | undefined,
  actionFilter: PlansActionFilter
): PlanCategoryEntry<T>[] {
  if (!Array.isArray(categories) || categories.length <= 0) {
    return []
  }

  return categories
    .map((category, categoryIndex) => {
      if (getVisiblePlanCategoryBucketKeys(category, actionFilter).length <= 0) {
        return null
      }
      return { category, categoryIndex }
    })
    .filter((entry): entry is PlanCategoryEntry<T> => entry != null)
}

function buildExpansionSyncTargets<T extends CategoryWithPlanBuckets>(
  categoryEntries: PlanCategoryEntry<T>[],
  actionFilter: PlansActionFilter
): ExpansionSyncTargets {
  const orderedCompositeKeys: string[] = []
  const defaultExpandedKeys: Record<string, boolean> = {}

  categoryEntries.forEach(({ category, categoryIndex }) => {
    const categoryKey = resolvePlanCategoryKey(category, categoryIndex)
    const visibleBucketKeys = getVisiblePlanCategoryBucketKeys(category, actionFilter)

    visibleBucketKeys.forEach((bucketKey) => {
      const bucketCompositeKey = buildPlanCategoryBucketCompositeKey({ categoryKey, bucketKey })
      orderedCompositeKeys.push(bucketCompositeKey)

      if (!shouldDefaultExpandBucket(actionFilter, bucketKey)) {
        return
      }

      defaultExpandedKeys[bucketCompositeKey] = true
    })
  })

  return { orderedCompositeKeys, defaultExpandedKeys }
}

/** ALL → sells only. Specific filter → every visible bucket of that filter. */
function shouldDefaultExpandBucket(
  actionFilter: PlansActionFilter,
  bucketKey: PlanCategoryBucketKey
): boolean {
  if (actionFilter === PlansActionFilter.ALL) {
    return bucketKey === PlanCategoryBucketKey.sellRecommendations
  }
  return true
}

function resolveScopeExpansionUpdate(
  previousExpandedKeys: Record<string, boolean>,
  { defaultExpandedKeys, orderedCompositeKeys }: ExpansionSyncTargets
): ExpansionScopeUpdate {
  const hasExistingExpansion = Object.values(previousExpandedKeys).some(Boolean)
  const nextExpandedKeys = hasExistingExpansion ? previousExpandedKeys : defaultExpandedKeys
  const scrollTarget = BUCKET_SCROLL_INTO_VIEW_ENABLED
    ? (orderedCompositeKeys.find((compositeKey) => nextExpandedKeys[compositeKey]) ?? null)
    : null

  return {
    nextExpandedKeys,
    scrollTarget,
    shouldUpdateKeys: !areExpandedBucketKeysEqual(previousExpandedKeys, nextExpandedKeys)
  }
}

function areExpandedBucketKeysEqual(previousKeys: Record<string, boolean>, nextKeys: Record<string, boolean>): boolean {
  const previousKeyNames = Object.keys(previousKeys)
  const nextKeyNames = Object.keys(nextKeys)

  if (previousKeyNames.length !== nextKeyNames.length) {
    return false
  }

  return previousKeyNames.every((keyName) => previousKeys[keyName] === nextKeys[keyName])
}

function scrollToExpandedBucket(bucketCompositeKey: string): void {
  if (!BUCKET_SCROLL_INTO_VIEW_ENABLED) {
    return
  }

  const bucketElement = document.querySelector(`[data-plan-category-bucket-key="${bucketCompositeKey}"]`)

  if (!(bucketElement instanceof HTMLElement)) {
    return
  }

  const scrollContainer = findScrollContainer(bucketElement)
  if (!scrollContainer) {
    return
  }

  if (getBucketVisibleRatioInContainer(bucketElement, scrollContainer) >= BUCKET_SCROLL_MIN_VISIBLE_RATIO) {
    return
  }

  const targetScrollTop = getBucketScrollTopForViewportRatio(
    bucketElement,
    scrollContainer,
    BUCKET_SCROLL_TARGET_VIEWPORT_RATIO
  )

  scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'auto' })
}

function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement

  while (parent) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return parent
    }
    parent = parent.parentElement
  }

  return null
}

function getBucketVisibleRatioInContainer(bucketElement: HTMLElement, scrollContainer: HTMLElement): number {
  const bucketRect = bucketElement.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  const visibleTop = Math.max(bucketRect.top, containerRect.top)
  const visibleBottom = Math.min(bucketRect.bottom, containerRect.bottom)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  const bucketHeight = bucketRect.height

  if (bucketHeight <= 0) {
    return 0
  }

  return visibleHeight / bucketHeight
}

function getBucketScrollTopForViewportRatio(
  bucketElement: HTMLElement,
  scrollContainer: HTMLElement,
  viewportRatio: number
): number {
  const bucketRect = bucketElement.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  const bucketTopInViewport = bucketRect.top - containerRect.top
  const targetTopInViewport = scrollContainer.clientHeight * viewportRatio
  const unclampedScrollTop = scrollContainer.scrollTop + bucketTopInViewport - targetTopInViewport
  const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight

  return Math.max(0, Math.min(unclampedScrollTop, maxScrollTop))
}
